import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, SimpleGrid, Loader } from '@mantine/core'
import { toast } from 'react-toastify'
import { Profile } from '../../app/models/profile'
import { useStore } from '../../app/stores/store'
import { AmmoScoreBadge, AppButton, AppText, ProfileUserAvatar } from '../../app/common/ui'
import agent from '../../app/api/agent'

interface FollowRowProps {
  user: Profile;
}

const FollowRow = observer(function FollowRow ({ user }: FollowRowProps) {
  const { userStore } = useStore()
  const history = useHistory()
  const [following, setFollowing] = useState(user.isFollowing ?? false)
  const [busy, setBusy] = useState(false)

  const isCurrentUser = userStore.user?.username === user.username
  const canFollow = userStore.isLoggedIn && !isCurrentUser

  const toggle = async () => {
    if (busy) return
    setBusy(true)
    try {
      if (following) {
        await agent.Profiles.unfollow(user.username)
        setFollowing(false)
      } else {
        await agent.Profiles.follow(user.username)
        setFollowing(true)
      }
    } catch {
      toast.error('Failed to update follow status.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 8,
        background: 'var(--gs-surface)',
        border: '1px solid var(--gs-border)',
        '&:hover': { borderColor: 'var(--gs-border-bright)' }
      }}
    >
      <ProfileUserAvatar
        variant="row"
        size={44}
        imageUrl={user.image}
        displayName={user.displayName}
        username={user.username}
        tier={user.ammoTier}
        onClick={() => history.push(`/profiles/${user.username}`)}
      />

      <Box
        sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
        onClick={() => history.push(`/profiles/${user.username}`)}
      >
        <AppText
          weight={600}
          size="sm"
          sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-text)', lineHeight: 1.3 }}
          lineClamp={1}
        >
          {user.displayName || user.username}
        </AppText>
        <AppText size="xs" sx={{ color: 'var(--gs-muted)' }}>
          @{user.username}
        </AppText>
        {user.ammoTier && (
          <Box mt={3}>
            <AmmoScoreBadge tier={user.ammoTier} score={user.ammoScore ?? 0} size="sm" />
          </Box>
        )}
      </Box>

      {canFollow && (
        <AppButton
          size="xs"
          variant={following ? 'outline' : 'filled'}
          color={following ? 'gray' : 'orange'}
          loading={busy}
          onClick={toggle}
          sx={{ flexShrink: 0, minWidth: 80 }}
        >
          {following ? 'Unfollow' : 'Follow'}
        </AppButton>
      )}
    </Box>
  )
})

interface Props {
  username: string;
  mode: 'followers' | 'following';
}

export default observer(function FollowList ({ username, mode }: Props) {
  const { profileStore } = useStore()
  const [list, setList] = useState<Profile[] | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    setList(null);
    (mode === 'followers'
      ? profileStore.loadFollowers(username)
      : profileStore.loadFollowing(username)
    )
      .then((data) => setList(data))
      .catch(() => setList([]))
      .finally(() => setLoading(false))
  }, [username, mode, profileStore])

  if (loading || list === null) {
    return (
      <Box py="xl" sx={{ display: 'flex', justifyContent: 'center' }}>
        <Loader size="sm" />
      </Box>
    )
  }

  if (list.length === 0) {
    return (
      <Box py="xl" sx={{ textAlign: 'center' }}>
        <AppText color="dimmed" size="sm">
          {mode === 'followers' ? 'No followers yet.' : 'Not following anyone yet.'}
        </AppText>
      </Box>
    )
  }

  return (
    <SimpleGrid cols={1} spacing="xs">
      {list.map((u) => (
        <FollowRow key={u.username} user={u} />
      ))}
    </SimpleGrid>
  )
})
